// Libs
import { FieldType, ViewModelBase, ApiResult } from "@shoothill/core";
import { action, computed, observable, runInAction } from "mobx";
import validator from "validator";

// App
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import { SaveStore } from "Globals/Stores/Domain";

// Models
import { CreditCardModel } from "Globals/Models/Domain/FormSections/IncomeSavingsAndOutgoings/CreditCardModel";
import { CreditCardDTO } from "Globals/Models/DTO/CreditCardDTO";

// ViewModels
import IncomeDetailsViewModel from "Globals/ViewModels/FormSections/IncomeSavingsAndOutgoings/IncomeDetailsViewModel";

export default class CreditCardViewModel extends ViewModelBase<CreditCardModel> {
    private saveStore: SaveStore = StoresInstance.Domain.SaveStore;

    constructor(creditCardModel?: CreditCardModel) {
        super(creditCardModel ? creditCardModel : new CreditCardModel());
    }

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<CreditCardModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @action
    public stash = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        let valid = await this.isModelValid();

        if (valid) {
            let model = new CreditCardModel();
            model.fromDto(this.model);
            let viewModel = new CreditCardViewModel(model);

            runInAction(() => {
                incomeDetails.creditCardViewModels.push(viewModel);
                // triggers the auto-save.
                incomeDetails.setDirty("id", true);
            });

            this.model.resetModel();
        }
    };

    @action
    public saveFromStash = async (incomeDetailId: string): Promise<void> => {
        try {
            this.setIsLoading(true);
            this.saveStore.setIsSaving(true);

            this.model.incomeDetailId = incomeDetailId;

            let dto: CreditCardDTO = this.model.toDto();
            const apiResult = await this.Post<CreditCardDTO>(AppUrls.Server.ApplicationForm.Upsert.CreditCard, dto);

            if (apiResult.wasSuccessful) {
                this.model.fromDto(apiResult.payload);
                console.info("CreditCard:Upsert-Successful");
                return Promise.resolve();
            } else {
                console.error("MoneyGifter:Upsert-Error", apiResult.errors);
                return Promise.reject(apiResult.errors);
            }
        } catch (exception) {
            // Set server validation error messages.
            console.log("Could not save from Stash.");
            console.log(exception);
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public delete = async (incomeDetails: IncomeDetailsViewModel): Promise<void> => {
        try {
            if (!this.model.getValue("incomeDetailId")) {
                console.log("A.");
                runInAction(() => {
                    let index = incomeDetails.creditCardViewModels.findIndex((vm) => vm.getValue("id") == this.model.id);
                    if (index > -1) {
                        incomeDetails.creditCardViewModels.splice(index, 1);
                    }
                });
            } else {
                console.log("B");
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                this.model.incomeDetailId = incomeDetails.model.id ? incomeDetails.model.id : "";
                this.model.isDeleted = true;

                let upsertCreditCard: CreditCardDTO = this.model.toDto();
                const apiResult = await this.Post<CreditCardDTO>(AppUrls.Server.ApplicationForm.Upsert.CreditCard, upsertCreditCard);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("CreditCard:Upsert-Successful");
                    let model = new CreditCardModel();
                    model.fromDto(apiResult.payload);

                    const filtered = incomeDetails.creditCardViewModels.filter((a) => a.model.isDeleted == false);
                    runInAction(() => {
                        incomeDetails.creditCardViewModels = filtered;
                    });
                    this.model.resetModel();
                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("CreditCard:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            // Set server validation error messages.
        } finally {
            this.saveStore.setIsSaving(false);
            this.setIsLoading(false);
        }
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<CreditCardModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
    };

    @computed
    public get isModelDirty(): boolean {
        let dirty = false;
        Object.keys(this.model).forEach((field) => {
            if (this.model.getDirty(field as keyof FieldType<CreditCardModel>)) {
                dirty = true;
            }
        });
        return dirty;
    }
    @action
    public setModelDirty(value: boolean): void {
        Object.keys(this.model).forEach((field) => {
            this.model.setDirty(field as keyof FieldType<CreditCardModel>, value);
        });
    }

    public async isFieldValid(fieldName: keyof FieldType<CreditCardModel>, value: any): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);
        if (fieldName == "lenderId") {
            if (value && !validator.isEmpty(value)) {
                if (value == "0") {
                    errorMessage = "Lender is required";
                    isValid = false;
                }
            }
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);

        return isValid;
    }
    public afterUpdate: undefined;
    public beforeUpdate: undefined;
}
